import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Account_Jobroles_Summary_Query,
  Get_All_Job_Role,
  User_Account_Query,
} from "../../services/userAPI";
import WelcomeBackIcon from "../../Images/welcomeBackDashboardIcon.png";
import UpgradeIcon from "../../Images/upgradeIcon.png";
import LiteDashboardIconRed from "../../Images/liteandgrowthdashboardIconRed.png";
import RolesOverviewEditIcon from "../../Images/liteandgrowthrolesoverviewIcon.png";
import TotalRolesIcon from "../../Images/liteandgrowthdashboardtotalrolesIcon.png";
import ActiveRolesIcon from "../../Images/liteandgrowthdashboardaActiveRoelsIcon.png";
import TotalUsersIcon from "../../Images/liteandgrowthdashboardaTotalUsersIcon.png";
import ActiveUsersIcon from "../../Images/liteandgrowthdashboardaActiveUsersIcon.png";

/* ------------------------------ Illustration ------------------------------- */

const BannerIllustration = () => (
  <img
    src={WelcomeBackIcon}
    alt=""
    className="h-[140px] hidden md:block shrink-0 object-contain"
  />
);

/* -------------------------------- Role card -------------------------------- */

const RoleCard = ({ role, userCount, onView, onEdit }) => {
  const logoUrl = role.account?.mainLogoUrl;
  const accountName = role.account?.name || "";

  return (
    <div
      className="relative bg-white rounded-[4px] p-3 flex flex-col items-start text-left gap-3 border border-black/20 transition"
      style={{ boxShadow: "0 4px 6px 0 rgba(0,0,0,0.15)" }}
    >
      <button
        type="button"
        onClick={onEdit}
        className="absolute top-3 right-3 h-9 w-9 rounded-md text-[#B0B3BA] hover:text-[#AD0101] flex items-center justify-center transition"
      >
        <img src={RolesOverviewEditIcon} alt="" className="h-38.5 w-38.5 object-contain" />
      </button>
      <div className="h-[44.34px] w-[44.34px] shrink-0 rounded-full border-[0.37px] border-[#757575] bg-white overflow-hidden flex items-center justify-center">
        {logoUrl ? (
          <img src={logoUrl} alt={accountName || "Client"} className="h-full w-full object-contain p-1" />
        ) : (
          <span className="text-[16px] font-semibold text-gray-400">
            {(role.name || "").charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div className="shrink-0 w-full">
        <span className="block text-[16px] font-semibold text-[#1F2020] truncate w-full">
          {role.name}
        </span>
        <span className="mt-1 block text-[12px] font-medium text-[#B7B6B6] truncate w-full">
          {userCount} Users
        </span>
      </div>
      <button
        type="button"
        onClick={onView}
        className="shrink-0 w-full h-8 rounded-[2.96px] bg-[#AD0101] text-white text-[12px] font-medium transition"
      >
        <span className="text-[14px]">View role</span>
      </button>
    </div>
  );
};

/* ------------------------------- Stat card --------------------------------- */

const StatCard = ({ icon, value, label }) => (
  <div
    className="bg-white rounded-[15px] p-5 flex flex-col gap-4 border border-[#E5E7EB] h-[152px]"
    style={{ boxShadow: "0 2px 10px 0 rgba(0,0,0,0.04)" }}
  >
    <img src={icon} alt="" className="h-9 w-9 object-contain" />
    <div>
      <span className="block text-[28px] leading-8 font-bold text-[#1F2020]">{value}</span>
      <span className="mt-1 block text-[13px] leading-[18px] text-[#888888]">{label}</span>
    </div>
  </div>
);

/* --------------------------------- Page ------------------------------------ */

const RoleManagementDashboard = () => {
  const navigate = useNavigate();
  const userName =
    localStorage.getItem("userName") ||
    localStorage.getItem("flutter.userName") ||
    "Admin";

  const {
    data: accountData,
    loading: accountLoading,
    error: accountError,
  } = useQuery(User_Account_Query, { fetchPolicy: "network-only" });

  const accountId = accountData?.currentUserAccount?.id
    ? parseInt(accountData.currentUserAccount.id)
    : undefined;

  const {
    data: rolesData,
    loading: rolesLoading,
    error: rolesError,
  } = useQuery(Get_All_Job_Role, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: "network-only",
  });

  const { data: summaryData, loading: summaryLoading } = useQuery(
    Account_Jobroles_Summary_Query,
    {
      variables: { accountId },
      skip: !accountId,
      fetchPolicy: "network-only",
    }
  );

  const summary = summaryData?.getAccountJobrolesSummary || {};
  const roles = rolesData?.getAllJobrole || [];
  const userCountByRole = (summary.activeJobroles || []).reduce((acc, item) => {
    const name = item?.jobrole?.name;
    if (name) acc[name.trim().toLowerCase()] = item.userCount ?? 0;
    return acc;
  }, {});

  const totalRoles = roles.length;
  const activeRoles = summary.totalActiveRoles ?? 0;
  const totalUsers = summary.totalUsers ?? 0;
  const activeUsers = summary.totalActiveUsers ?? 0;
  const account = accountData?.currentUserAccount;

  return (
    <div className="min-h-screen flex px-4 pt-[88px] pb-6 gap-6">
      {/* Left rail */}
      <aside
        className="w-[232px] shrink-0 bg-white rounded-[12px] p-4 flex flex-col justify-between self-stretch"
        style={{ boxShadow: "0 20px 24px -4px rgba(0,0,0,0.08), 0 8px 8px -4px rgba(0,0,0,0.04)" }}
      >
        <button
          type="button"
          className="w-full h-10 flex items-center gap-2 px-4 rounded-md border border-[#DE9999] bg-[#F7E6E6] text-[#AD0101] text-[14px] leading-[21px] font-medium tracking-[0.1px]"
        >
          <img src={LiteDashboardIconRed} alt="" className="h-4 w-4 object-contain" />
          Dashboard
        </button>

        {/* Upgrade card */}
        <div className="rounded-[15px] bg-[#FFF2F2] border border-[#EFCCCC] p-4 flex flex-col items-start text-left gap-3 mt-6">
          <span className="h-10 w-10 rounded-full bg-white flex items-center justify-center">
            <img src={UpgradeIcon} alt="" className="h-40 w-40 object-contain" />
          </span>
          <span className="text-[16px] leading-6 font-bold text-[#101828]">Upgrade Plan</span>
          <span className="text-[10px] leading-[18px] font-normal text-[#6A7282]">
            Discover more features and maximize your productivity
          </span>
          <button
            type="button"
            className="w-full h-[31px] rounded-[4px] bg-[#AD0101] border border-[#AD0101] px-4 text-white text-[13px] font-medium transition"
          >
            Upgrade Now
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-x-hidden">
        {/* Welcome banner */}
        <div
          className="rounded-[15px] h-[180px] pl-8 pr-[118px] py-4 flex items-center justify-between"
          style={{
            background: "rgba(250, 201, 202, 0.37)",
            boxShadow: "0 4px 8px -2px rgba(0,0,0,0.04), 0 2px 4px -2px rgba(0,0,0,0.08)",
          }}
        >
          <div>
            <span className="block text-[12px] leading-[18px] font-bold tracking-[1.2px] text-[#393939] mb-2">
              Admin <span className="text-[#DE9999]">·</span> Role Management
            </span>
            <span className="block text-[36px] leading-[35px] font-bold tracking-[-0.7px] text-[#1F2020]">
              Welcome back, {userName}
            </span>
            <span className="mt-2 block text-[14px] leading-[22.75px] font-medium text-[#888888] max-w-[448px]">
              Create, configure, and manage roles, permissions, and access levels from one
              centralized workspace.
            </span>
          </div>
          <BannerIllustration />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mt-5">
          <StatCard
            icon={TotalRolesIcon}
            value={accountLoading || rolesLoading ? "…" : totalRoles}
            label="Total Roles"
          />
          <StatCard
            icon={ActiveRolesIcon}
            value={summaryLoading ? "…" : activeRoles}
            label="Active Roles"
          />
          <StatCard
            icon={TotalUsersIcon}
            value={summaryLoading ? "…" : totalUsers}
            label="Total Users"
          />
          <StatCard
            icon={ActiveUsersIcon}
            value={summaryLoading ? "…" : activeUsers}
            label="Active Users"
          />
        </div>

        {/* Roles Overview */}
        <div
          className="bg-white rounded-[15px] p-4 mt-5 border border-[#E5E7EB] space-y-5"
          style={{ boxShadow: "0 2px 10px 0 rgba(0,0,0,0.04)" }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-[16px] font-semibold leading-[24px] text-[#1F2020]">Roles Overview</h2>
            <button
              type="button"
              onClick={() => {
                if (account?.id) {
                  navigate(`/edit-client-and-role/${account.id}`, {
                    state: { addRoleOnly: true },
                  });
                } else {
                  navigate("/add-client-and-role");
                }
              }}
              className="h-9 px-5 rounded-md bg-[#AD0101] hover:bg-[#8C0101] text-white text-[13px] font-medium transition"
            >
              Add Role
            </button>
          </div>

          {/* Summary bar */}
          <div
            className="rounded-lg bg-[#FFF1F2] px-4 py-3 flex items-center justify-between"
            style={{ boxShadow: "0 4px 8px -2px rgba(0,0,0,0.04), 0 2px 4px -2px rgba(0,0,0,0.08)" }}
          >
            <div className="flex items-center gap-2.5">
              <span className="h-10 w-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                <img src={UpgradeIcon} alt="" className="h-50 w-50 object-contain" />
              </span>
              <div className="leading-tight flex flex-col">
                <span className="text-[10px] leading-[14px] text-[#888888]">Total Roles</span>
                <span className="text-[14px] leading-[20px] font-semibold text-[#1F2020]">
                  {accountLoading || rolesLoading ? "…" : totalRoles}
                </span>
              </div>
            </div>
            <div className="text-right leading-tight flex flex-col">
              <span className="text-[10px] leading-[14px] text-[#888888]">Total Users</span>
              <span className="text-[14px] leading-[20px] font-semibold text-[#1F2020]">
                {summaryLoading ? "…" : totalUsers}
              </span>
            </div>
          </div>

          {/* Role cards */}
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(262.5px, 1fr))" }}>
            {(accountLoading || rolesLoading) && (
              <div className="col-span-full py-10 flex justify-center">
                <div className="h-8 w-8 border-4 border-[#AD0101] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {(accountError || rolesError) && (
              <div className="col-span-full py-10 text-center text-[13px] text-[#AD0101]">
                Failed to load roles: {(accountError || rolesError)?.message}
              </div>
            )}
            {!accountLoading && !rolesLoading && !accountError && !rolesError && roles.length === 0 && (
              <div className="col-span-full py-10 text-center text-[13px] text-[#888888]">
                No roles found. Click "Add Role" to create a client with a role.
              </div>
            )}
            {!rolesLoading &&
              !rolesError &&
              roles.map((role) => (
                <RoleCard
                  key={role.id}
                  role={role}
                  userCount={
                    userCountByRole[(role.name || "").trim().toLowerCase()] ?? 0
                  }
                  onView={() =>
                    navigate(`/role-master-configuration?roleId=${role.id}&accountId=${role.account?.id ?? account?.id}`, {
                      state: {
                        roleId: role.id,
                        accountId: role.account?.id ?? account?.id,
                        roleName: role.name,
                        accountName: role.account?.name ?? account?.name,
                      },
                    })
                  }
                  onEdit={() =>
                    navigate(`/edit-client-and-role/${role.account?.id ?? account?.id}`, {
                      state: { role },
                    })
                  }
                />
              ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RoleManagementDashboard;
